import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {
  AccountingAuditRow,
  AccountingExpense,
  AccountingExpensePayment,
  AccountingInvoiceFollowUp,
  AccountingPaymentRow,
  AccountingPrintEvent,
  AccountingTicketRow,
} from '../../../models/accounting'
import {AccountingApiError} from './errors'
import {accountingScopeQueryKey, parseInvoiceFollowUpListResponse} from './helpers'
import {
  addAccountingTicketPrintEvent,
  archiveAccountingExpenseAttachment,
  attachAccountingExpenseInvoice,
  cancelAccountingExpense,
  contactAccountingInvoiceFollowUp,
  createAccountingExpense,
  fetchAccountingAudit,
  fetchAccountingExpenseAttachments,
  fetchAccountingExpenseById,
  fetchAccountingExpenses,
  fetchAccountingInvoiceFollowUpById,
  fetchAccountingInvoiceFollowUps,
  fetchAccountingPayments,
  fetchAccountingReport,
  fetchAccountingSummary,
  fetchAccountingTejPending,
  fetchAccountingTicketById,
  fetchAccountingTicketPrintHistory,
  fetchAccountingTickets,
  fetchAccountingWithholdingPending,
  payAccountingExpense,
  reviewAccountingExpenseWithholding,
  scheduleAccountingInvoiceFollowUp,
  updateAccountingExpense,
  updateAccountingExpenseTej,
  uploadAccountingExpenseAttachment,
  validateAccountingExpense,
} from './service'
import {AccountingListState, AccountingState} from './types'

function emptyList<T>(): AccountingListState<T> {
  return {
    items: [],
    total: 0,
    totalPages: 0,
    page: 1,
    limit: 20,
    loading: false,
    error: null,
    errorCode: null,
    paginated: false,
    hasNextPage: false,
    hasPreviousPage: false,
    stats: null,
    statsAvailable: false,
    statsLoading: false,
    statsScopeKey: '',
    requestId: null,
  }
}

const initialState: AccountingState = {
  summary: null,
  summaryLoading: false,
  summaryError: null,
  payments: emptyList<AccountingPaymentRow>(),
  expenses: emptyList<AccountingExpense>(),
  expenseDetail: null,
  expensePayments: [],
  expenseActionLoading: false,
  tickets: emptyList<AccountingTicketRow>(),
  ticketDetail: null,
  printEvents: [],
  ticketActionLoading: false,
  report: null,
  reportLoading: false,
  reportError: null,
  audit: emptyList<AccountingAuditRow>(),
  followUps: emptyList<AccountingInvoiceFollowUp>(),
  followUpDetail: null,
  withholdingPending: emptyList<AccountingExpense>(),
  tejPending: emptyList<AccountingExpense>(),
  expenseAttachments: [],
  expenseAttachmentsLoading: false,
}

function errPayload(action: PayloadAction<any>): AccountingApiError {
  return (action.payload || {}) as AccountingApiError
}

function applyListPending<T>(state: AccountingListState<T>) {
  state.loading = true
  state.error = null
  state.errorCode = null
}

function applyListRejected<T>(state: AccountingListState<T>, action: PayloadAction<any>) {
  const payload = errPayload(action)
  state.loading = false
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
}

const accountingSlice = createSlice({
  name: 'accounting',
  initialState,
  reducers: {
    clearAccountingExpenseDetail(state) {
      state.expenseDetail = null
      state.expensePayments = []
      state.expenseAttachments = []
    },
    clearAccountingFollowUpDetail(state) {
      state.followUpDetail = null
    },
    clearAccountingTicketDetail(state) {
      state.ticketDetail = null
      state.printEvents = []
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccountingSummary.pending, (state) => {
        state.summaryLoading = true
        state.summaryError = null
      })
      .addCase(fetchAccountingSummary.fulfilled, (state, action) => {
        state.summaryLoading = false
        state.summary = action.payload || null
      })
      .addCase(fetchAccountingSummary.rejected, (state, action) => {
        if (action.meta.aborted) {
          state.summaryLoading = false
          return
        }
        state.summaryLoading = false
        state.summaryError = errPayload(action).error || 'Request failed'
      })
      .addCase(fetchAccountingPayments.pending, (state) => applyListPending(state.payments))
      .addCase(fetchAccountingPayments.fulfilled, (state, action) => {
        const data = action.payload || {}
        state.payments.loading = false
        state.payments.items = data.payments || []
        state.payments.total = data.total || 0
        state.payments.totalPages = data.totalPages || 0
        state.payments.page = data.page || 1
      })
      .addCase(fetchAccountingPayments.rejected, (state, action) =>
        applyListRejected(state.payments, action)
      )
      .addCase(fetchAccountingExpenses.pending, (state) => applyListPending(state.expenses))
      .addCase(fetchAccountingExpenses.fulfilled, (state, action) => {
        const data = action.payload || {}
        state.expenses.loading = false
        state.expenses.items = data.expenses || []
        state.expenses.total = data.total || 0
        state.expenses.totalPages = data.totalPages || 0
        state.expenses.page = data.page || 1
      })
      .addCase(fetchAccountingExpenses.rejected, (state, action) => {
        if (action.meta.aborted) {
          state.expenses.loading = false
          return
        }
        applyListRejected(state.expenses, action)
      })
      .addCase(fetchAccountingExpenseById.fulfilled, (state, action) => {
        const data = action.payload || {}
        state.expenseDetail = data.expense || null
        state.expensePayments = data.payments || []
        if (data.attachments || data.expense?.attachments) {
          state.expenseAttachments = data.attachments || data.expense.attachments || []
        }
      })
      .addCase(createAccountingExpense.pending, (state) => {
        state.expenseActionLoading = true
      })
      .addCase(createAccountingExpense.fulfilled, (state, action) => {
        state.expenseActionLoading = false
        state.expenseDetail = action.payload?.expense || action.payload || null
      })
      .addCase(createAccountingExpense.rejected, (state) => {
        state.expenseActionLoading = false
      })
      .addCase(updateAccountingExpense.pending, (state) => {
        state.expenseActionLoading = true
      })
      .addCase(updateAccountingExpense.fulfilled, (state, action) => {
        state.expenseActionLoading = false
        state.expenseDetail = action.payload?.expense || state.expenseDetail
      })
      .addCase(updateAccountingExpense.rejected, (state) => {
        state.expenseActionLoading = false
      })
      .addCase(validateAccountingExpense.pending, (state) => {
        state.expenseActionLoading = true
      })
      .addCase(validateAccountingExpense.fulfilled, (state, action) => {
        state.expenseActionLoading = false
        state.expenseDetail = action.payload?.expense || state.expenseDetail
      })
      .addCase(validateAccountingExpense.rejected, (state) => {
        state.expenseActionLoading = false
      })
      .addCase(payAccountingExpense.pending, (state) => {
        state.expenseActionLoading = true
      })
      .addCase(payAccountingExpense.fulfilled, (state, action) => {
        state.expenseActionLoading = false
        if (action.payload?.expense) state.expenseDetail = action.payload.expense
        if (action.payload?.payments) state.expensePayments = action.payload.payments
      })
      .addCase(payAccountingExpense.rejected, (state) => {
        state.expenseActionLoading = false
      })
      .addCase(cancelAccountingExpense.pending, (state) => {
        state.expenseActionLoading = true
      })
      .addCase(cancelAccountingExpense.fulfilled, (state, action) => {
        state.expenseActionLoading = false
        state.expenseDetail = action.payload?.expense || state.expenseDetail
      })
      .addCase(cancelAccountingExpense.rejected, (state) => {
        state.expenseActionLoading = false
      })
      .addCase(fetchAccountingTickets.pending, (state) => applyListPending(state.tickets))
      .addCase(fetchAccountingTickets.fulfilled, (state, action) => {
        const data = action.payload || {}
        state.tickets.loading = false
        state.tickets.items = data.tickets || []
        state.tickets.total = data.total || 0
        state.tickets.totalPages = data.totalPages || 0
        state.tickets.page = data.page || 1
      })
      .addCase(fetchAccountingTickets.rejected, (state, action) =>
        applyListRejected(state.tickets, action)
      )
      .addCase(fetchAccountingTicketById.fulfilled, (state, action) => {
        const data = action.payload || {}
        state.ticketDetail = data.ticket || null
        state.printEvents = data.printHistory || []
      })
      .addCase(fetchAccountingTicketPrintHistory.fulfilled, (state, action) => {
        const data = action.payload || {}
        state.printEvents = data.events || data.printHistory || []
      })
      .addCase(addAccountingTicketPrintEvent.pending, (state) => {
        state.ticketActionLoading = true
      })
      .addCase(addAccountingTicketPrintEvent.fulfilled, (state, action) => {
        state.ticketActionLoading = false
        const event = action.payload?.event as AccountingPrintEvent | undefined
        if (event) state.printEvents = [...state.printEvents, event]
      })
      .addCase(addAccountingTicketPrintEvent.rejected, (state) => {
        state.ticketActionLoading = false
      })
      .addCase(fetchAccountingReport.pending, (state, action) => {
        const format = action.meta.arg?.format
        if (format && format !== 'json') return
        state.reportLoading = true
        state.reportError = null
      })
      .addCase(fetchAccountingReport.fulfilled, (state, action) => {
        const data = action.payload || {}
        if (data.format && data.format !== 'json') return
        state.reportLoading = false
        state.report = data.report || data
      })
      .addCase(fetchAccountingReport.rejected, (state, action) => {
        const format = action.meta.arg?.format
        if (format && format !== 'json') return
        state.reportLoading = false
        state.reportError = errPayload(action).error || 'Request failed'
      })
      .addCase(fetchAccountingAudit.pending, (state) => applyListPending(state.audit))
      .addCase(fetchAccountingAudit.fulfilled, (state, action) => {
        const data = action.payload || {}
        state.audit.loading = false
        state.audit.items = data.logs || []
        state.audit.total = data.total || 0
        state.audit.page = data.page || 1
        const limit = 50
        state.audit.totalPages = Math.ceil((data.total || 0) / limit) || 0
      })
      .addCase(fetchAccountingAudit.rejected, (state, action) => applyListRejected(state.audit, action))
      .addCase(fetchAccountingInvoiceFollowUps.pending, (state, action) => {
        applyListPending(state.followUps)
        state.followUps.requestId = action.meta.requestId
        const scopeKey = accountingScopeQueryKey(action.meta.arg || {})
        if (scopeKey !== state.followUps.statsScopeKey) {
          state.followUps.statsLoading = true
        }
      })
      .addCase(fetchAccountingInvoiceFollowUps.fulfilled, (state, action) => {
        if (state.followUps.requestId && action.meta.requestId !== state.followUps.requestId) return
        const parsed = parseInvoiceFollowUpListResponse(action.payload)
        state.followUps.loading = false
        state.followUps.items = parsed.items
        state.followUps.paginated = parsed.paginated
        state.followUps.page = parsed.page
        state.followUps.limit = parsed.limit
        state.followUps.total = parsed.total
        state.followUps.totalPages = parsed.totalPages
        state.followUps.hasNextPage = parsed.hasNextPage
        state.followUps.hasPreviousPage = parsed.hasPreviousPage
        state.followUps.statsLoading = false
        state.followUps.statsAvailable = parsed.statsAvailable
        state.followUps.stats = parsed.stats
        state.followUps.statsScopeKey = accountingScopeQueryKey(action.meta.arg || {})
      })
      .addCase(fetchAccountingInvoiceFollowUps.rejected, (state, action) => {
        if (action.meta.aborted || (state.followUps.requestId && action.meta.requestId !== state.followUps.requestId)) {
          state.followUps.loading = false
          return
        }
        state.followUps.statsLoading = false
        applyListRejected(state.followUps, action)
      })
      .addCase(fetchAccountingInvoiceFollowUpById.fulfilled, (state, action) => {
        state.followUpDetail = action.payload?.followUp || action.payload || null
      })
      .addCase(contactAccountingInvoiceFollowUp.fulfilled, (state, action) => {
        state.expenseActionLoading = false
        const followUp = action.payload?.followUp || action.payload
        if (followUp?.id) {
          state.followUpDetail = followUp
          state.followUps.items = state.followUps.items.map((row) =>
            row.id === followUp.id ? followUp : row
          )
        }
      })
      .addCase(contactAccountingInvoiceFollowUp.pending, (state) => {
        state.expenseActionLoading = true
      })
      .addCase(contactAccountingInvoiceFollowUp.rejected, (state) => {
        state.expenseActionLoading = false
      })
      .addCase(scheduleAccountingInvoiceFollowUp.pending, (state) => {
        state.expenseActionLoading = true
      })
      .addCase(scheduleAccountingInvoiceFollowUp.fulfilled, (state, action) => {
        state.expenseActionLoading = false
        const followUp = action.payload?.followUp || action.payload
        if (followUp?.id) {
          state.followUpDetail = followUp
          state.followUps.items = state.followUps.items.map((row) =>
            row.id === followUp.id ? followUp : row
          )
        }
      })
      .addCase(scheduleAccountingInvoiceFollowUp.rejected, (state) => {
        state.expenseActionLoading = false
      })
      .addCase(attachAccountingExpenseInvoice.pending, (state) => {
        state.expenseActionLoading = true
      })
      .addCase(attachAccountingExpenseInvoice.fulfilled, (state, action) => {
        state.expenseActionLoading = false
        if (action.payload?.expense) state.expenseDetail = action.payload.expense
        const followUp = action.payload?.followUp
        if (followUp?.id) {
          state.followUps.items = state.followUps.items.map((row) =>
            row.id === followUp.id ? followUp : row
          )
        }
      })
      .addCase(attachAccountingExpenseInvoice.rejected, (state) => {
        state.expenseActionLoading = false
      })
      .addCase(reviewAccountingExpenseWithholding.pending, (state) => {
        state.expenseActionLoading = true
      })
      .addCase(reviewAccountingExpenseWithholding.fulfilled, (state, action) => {
        state.expenseActionLoading = false
        if (action.payload?.expense) state.expenseDetail = action.payload.expense
      })
      .addCase(reviewAccountingExpenseWithholding.rejected, (state) => {
        state.expenseActionLoading = false
      })
      .addCase(updateAccountingExpenseTej.pending, (state) => {
        state.expenseActionLoading = true
      })
      .addCase(updateAccountingExpenseTej.fulfilled, (state, action) => {
        state.expenseActionLoading = false
        if (action.payload?.expense) state.expenseDetail = action.payload.expense
        const expense = action.payload?.expense
        if (expense?.id) {
          state.tejPending.items = state.tejPending.items.map((row) =>
            row.id === expense.id ? expense : row
          )
        }
      })
      .addCase(updateAccountingExpenseTej.rejected, (state) => {
        state.expenseActionLoading = false
      })
      .addCase(fetchAccountingWithholdingPending.pending, (state) =>
        applyListPending(state.withholdingPending)
      )
      .addCase(fetchAccountingWithholdingPending.fulfilled, (state, action) => {
        const data = action.payload || {}
        state.withholdingPending.loading = false
        state.withholdingPending.items = data.expenses || data.items || []
        state.withholdingPending.total = data.total || state.withholdingPending.items.length
      })
      .addCase(fetchAccountingWithholdingPending.rejected, (state, action) =>
        applyListRejected(state.withholdingPending, action)
      )
      .addCase(fetchAccountingTejPending.pending, (state) => applyListPending(state.tejPending))
      .addCase(fetchAccountingTejPending.fulfilled, (state, action) => {
        const data = action.payload || {}
        state.tejPending.loading = false
        state.tejPending.items = data.expenses || data.items || []
        state.tejPending.total = data.total || state.tejPending.items.length
        state.tejPending.totalPages =
          data.totalPages ||
          Math.ceil((state.tejPending.total || 0) / (Number(action.meta.arg?.limit) || state.tejPending.items.length || 1)) ||
          0
      })
      .addCase(fetchAccountingTejPending.rejected, (state, action) =>
        applyListRejected(state.tejPending, action)
      )
      .addCase(fetchAccountingExpenseAttachments.pending, (state) => {
        state.expenseAttachmentsLoading = true
      })
      .addCase(fetchAccountingExpenseAttachments.fulfilled, (state, action) => {
        state.expenseAttachmentsLoading = false
        const data = action.payload || {}
        state.expenseAttachments = data.attachments || data.items || []
        if (state.expenseDetail) {
          state.expenseDetail.attachments = state.expenseAttachments
        }
      })
      .addCase(fetchAccountingExpenseAttachments.rejected, (state) => {
        state.expenseAttachmentsLoading = false
      })
      .addCase(uploadAccountingExpenseAttachment.pending, (state) => {
        state.expenseActionLoading = true
      })
      .addCase(uploadAccountingExpenseAttachment.fulfilled, (state, action) => {
        state.expenseActionLoading = false
        const data = action.payload || {}
        if (data.expense) state.expenseDetail = data.expense
        if (data.attachments) state.expenseAttachments = data.attachments
        else if (data.attachment) {
          state.expenseAttachments = [...state.expenseAttachments, data.attachment]
        }
        if (state.expenseDetail) state.expenseDetail.attachments = state.expenseAttachments
      })
      .addCase(uploadAccountingExpenseAttachment.rejected, (state) => {
        state.expenseActionLoading = false
      })
      .addCase(archiveAccountingExpenseAttachment.pending, (state) => {
        state.expenseActionLoading = true
      })
      .addCase(archiveAccountingExpenseAttachment.fulfilled, (state, action) => {
        state.expenseActionLoading = false
        const data = action.payload || {}
        if (data.expense) state.expenseDetail = data.expense
        if (data.attachments) state.expenseAttachments = data.attachments
        if (state.expenseDetail) state.expenseDetail.attachments = state.expenseAttachments
      })
      .addCase(archiveAccountingExpenseAttachment.rejected, (state) => {
        state.expenseActionLoading = false
      })
  },
})

export const {
  clearAccountingExpenseDetail,
  clearAccountingTicketDetail,
  clearAccountingFollowUpDetail,
} = accountingSlice.actions
export default accountingSlice.reducer
